import type {
  HsFrustumRequestInfo,
  HsFrustumView,
  HsMaterialView,
  HsNodeSmallView,
  HsOriginalMeshSource,
} from '@sibe/hyperscene';
import type { HsGeometryPayloadKind } from './geometry-payload-cache.class';
import type { HsGeometryPayloadRequest, HsSceneSourceApi } from './scene-source.class';

type HsViewerGeometryKind = 'Meshes' | 'Lines' | 'PointClouds';
type HsViewerGeometryFormat = 'binary' | 'draco' | 'quantized' | 'quantized2';

/**
 * The `HsViewerApi` (`@sibe/hyperscene/api/viewer`) methods a scene source reads. Addons do not import each
 * other, so the scene source names them structurally.
 */
export interface HsSceneViewerApi {
  getGeometryPayload(
    kind: HsViewerGeometryKind,
    geometryIds: readonly string[],
    options?: { format?: HsViewerGeometryFormat; signal?: AbortSignal }
  ): Promise<ArrayBuffer>;
  getFrustum(
    sceneId: string,
    request: HsFrustumRequestInfo,
    options?: { signal?: AbortSignal }
  ): Promise<HsFrustumView>;
  getMaterials(materialIds: readonly string[]): Promise<HsMaterialView[]>;
  getNodeTreeOriginalMaterials(rootNodeId: string): Promise<HsMaterialView[]>;
  getSceneRootNodes(sceneId: string): Promise<HsNodeSmallView[]>;
  getDocuments(documentIds: readonly string[]): Promise<Map<string, Blob>>;
  getDocument(documentId: string): Promise<Blob>;
  getOriginalMeshSources(nodeIds: readonly string[]): Promise<HsOriginalMeshSource[]>;
}

const VIEWER_GEOMETRY_KINDS: Record<HsGeometryPayloadKind, HsViewerGeometryKind> = {
  meshes: 'Meshes',
  lines: 'Lines',
  pointClouds: 'PointClouds',
};

/** Reads a scene source's data through an `HsViewerApi`: `new HsSceneSource({ api: hsSceneSourceApi(api) })`. */
export function hsSceneSourceApi(api: HsSceneViewerApi): HsSceneSourceApi {
  return {
    getGeometryPayload: (
      kind: HsGeometryPayloadKind,
      geometryIds: readonly string[],
      request: HsGeometryPayloadRequest
    ): Promise<ArrayBuffer> =>
      api.getGeometryPayload(VIEWER_GEOMETRY_KINDS[kind], geometryIds, {
        format: request.format as HsViewerGeometryFormat | undefined,
        signal: request.signal,
      }),
    getSceneFrustum: (sceneId: string, request: HsFrustumRequestInfo, signal?: AbortSignal): Promise<HsFrustumView> =>
      api.getFrustum(sceneId, request, { signal }),
    getMaterials: (materialIds: readonly string[]) => api.getMaterials(materialIds),
    getNodeTreeOriginalMaterials: (rootNodeId: string) => api.getNodeTreeOriginalMaterials(rootNodeId),
    getSceneRootNodes: (sceneId: string) => api.getSceneRootNodes(sceneId),
    getDocuments: (documentIds: readonly string[]) => api.getDocuments(documentIds),
    getDocument: (documentId: string) => api.getDocument(documentId),
    getOriginalMeshSources: (nodeIds: readonly string[]) => api.getOriginalMeshSources(nodeIds),
  };
}
